import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ClipboardList, Users, Box, Boxes, Cog, QrCode, FileText, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  orders: { label: "Ordens", icon: ClipboardList, color: "bg-blue-100 text-blue-700" },
  clients: { label: "Clientes", icon: Users, color: "bg-purple-100 text-purple-700" },
  parts: { label: "Peças", icon: Box, color: "bg-emerald-100 text-emerald-700" },
  molds: { label: "Moldes", icon: Boxes, color: "bg-amber-100 text-amber-700" },
  machines: { label: "Máquinas", icon: Cog, color: "bg-indigo-100 text-indigo-700" },
  box_labels: { label: "Etiquetas Caixa", icon: QrCode, color: "bg-rose-100 text-rose-700" },
  sales_guides: { label: "Guias Venda", icon: FileText, color: "bg-slate-100 text-slate-700" },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/search/global?q=${encodeURIComponent(query)}`);
        setResults(r.data);
      } catch (e) { setResults(null); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const goTo = (url) => {
    navigate(url);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-input bg-background hover:bg-secondary transition-colors text-sm text-muted-foreground min-w-[220px]"
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Pesquisar tudo...</span>
        <kbd className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] rounded border border-slate-300 font-mono">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-16 px-4">
          <div ref={containerRef} className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Ordens, clientes, peças, moldes, máquinas, etiquetas, guias..."
                className="flex-1 bg-transparent outline-none text-base"
                data-testid="global-search-input"
              />
              {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {!query && (
                <div className="text-center py-10 text-sm text-muted-foreground">
                  <p>Comece a escrever para pesquisar</p>
                  <p className="text-xs mt-1">Encontra por código, descrição, cliente, lote...</p>
                </div>
              )}
              {query && query.length < 2 && (
                <div className="text-center py-10 text-sm text-muted-foreground">Mínimo 2 caracteres</div>
              )}
              {results && results.total === 0 && (
                <div className="text-center py-10 text-sm text-muted-foreground">Sem resultados para <strong>{results.query}</strong></div>
              )}
              {results && Object.entries(results.results || {}).map(([category, items]) => {
                const meta = CATEGORY_META[category] || { label: category, icon: FileText, color: "bg-slate-100" };
                const Icon = meta.icon;
                return (
                  <div key={category} className="mb-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <Icon className="h-3 w-3" />
                      {meta.label} ({items.length})
                    </div>
                    {items.map(item => (
                      <button
                        key={item.id}
                        onClick={() => goTo(item.url)}
                        className="w-full flex items-start gap-3 px-3 py-2 rounded hover:bg-slate-100 text-left transition-colors group"
                        data-testid={`search-result-${category}-${item.code}`}
                      >
                        <div className={`h-8 w-8 rounded ${meta.color} flex items-center justify-center shrink-0`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800 group-hover:text-blue-700 truncate">{item.label}</div>
                          {item.sublabel && <div className="text-xs text-slate-500 truncate">{item.sublabel}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
              {results && results.total > 0 && (
                <div className="text-[10px] text-center text-slate-400 py-2 border-t">
                  {results.total} resultado(s) · Enter para abrir · ESC para fechar
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
